import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Download, Pencil, Send } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { api } from "@/lib/api";
import { Card, CardTitle, Logo, StatusBadge, btn, Avatar, avatarUrl } from "@/components/common";
import { LineItems, PaymentSchedule, QuoteTerms } from "@/components/TripSections";
import { InvoiceBuilder } from "@/components/InvoiceBuilder";
import { ItineraryEditor } from "@/components/ItineraryEditor";
import { NewQuoteDialog } from "@/components/NewQuoteDialog";
import { PaymentPlanEditor } from "@/components/PaymentPlanEditor";
import { CommissionCard } from "@/components/CommissionCard";
import { ConvertButton } from "@/pages/agent/TripWorkspace";
import { fmtDate, fmtMoney, itinerary, paymentSchedule, returnDate, tripTotal } from "@/lib/trip";

function SectionHead({ title, editing, onEdit, testId }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-3">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{title}</h2>
      {!editing && <button onClick={onEdit} data-testid={testId} className={btn.ghost}><Pencil className="h-3.5 w-3.5" />Edit</button>}
    </div>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const { trip: get, client, settings, send } = useDemo();
  const [editInvoice, setEditInvoice] = useState(false);
  const [editItin, setEditItin] = useState(false);
  const [editPlan, setEditPlan] = useState(false);
  const [editDetails, setEditDetails] = useState(false);
  const t = get(id);
  if (!t) return <Navigate to="/agent/quotes" replace />;
  const c = client(t.clientId);
  const deposit = paymentSchedule(t)[0].amount;

  const sendToClient = async () => {
    send(c.id, "agent", `Hi ${c.name.split(" ")[0]}, your quote ${t.quoteNumber} for ${t.title} is ready to review in MyTrip. Total ${fmtMoney(tripTotal(t), true)}, with a ${fmtMoney(deposit, true)} deposit to confirm.`, t.id);
    try {
      const { data } = await api.post(`/trips/${t.id}/send-quote`);
      if (data.emailed) toast.success("Quote sent", { description: `Message posted and the quote PDF was emailed to ${c.name}.` });
      else toast.success("Quote sent", { description: `Message posted. No email is on file for ${c.name}, so the PDF wasn't emailed.` });
    } catch {
      toast.success("Quote sent", { description: `Added to your conversation with ${c.name}.` });
      toast.error("Couldn't email the quote PDF — please try again.");
    }
  };

  return (
    <div data-testid="quote-detail">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Link to="/agent/quotes" data-testid="back-to-quotes" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-navy-900"><ArrowLeft className="h-4 w-4" />All Quotes</Link>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEditDetails(true)} data-testid="quote-edit-details-btn" className={btn.secondary}><Pencil className="h-4 w-4" />Edit Details</button>
          <button onClick={() => window.print()} data-testid="quote-download-btn" className={btn.secondary}><Download className="h-4 w-4" />PDF / Print</button>
          <button onClick={sendToClient} data-testid="quote-send-btn" className={btn.secondary}><Send className="h-4 w-4" />Send to Client</button>
          <ConvertButton trip={t} />
        </div>
      </div>
      {t.status !== "QUOTE" && (
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="quote-converted-banner">
          <p className="flex items-center gap-2 text-sm font-medium text-emerald-800"><CheckCircle2 className="h-4 w-4" />Converted to trip · booking <span className="font-mono">{t.bookingNumber}</span></p>
          <Link to={`/agent/trips/${t.id}`} data-testid="quote-open-trip-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:underline">Open trip workspace <ArrowRight className="h-4 w-4" /></Link>
        </div>
      )}
      <Card id="quote-printable" className="mx-auto max-w-4xl overflow-hidden">
        <div className="flex flex-col gap-6 border-b border-slate-100 p-6 sm:flex-row sm:items-start sm:justify-between sm:p-8">
          <div>
            <Logo brand="mytrip" sub="Powered by Topsail Travel" />
            <div className="mt-4 flex items-center gap-3">
              <Avatar name={settings.advisorName} src={avatarUrl(settings.advisorId, settings.advisorAvatar)} size="md" data-testid="quote-advisor-photo" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy-900">{settings.advisorName}</p>
                <p className="truncate text-xs text-slate-500">{settings.advisorTitle ? `${settings.advisorTitle} · ` : ""}{settings.advisorEmail}</p>
              </div>
            </div>
          </div>
          <div className="sm:text-right">
            <div className="flex items-center gap-2 sm:justify-end"><p className="font-mono text-sm text-navy-900" data-testid="quote-number">{t.quoteNumber}</p><StatusBadge status={t.status} /></div>
            <p className="mt-2 text-xs text-slate-500">Prepared {fmtDate(t.createdOn || t.bookedOn)}</p>
          </div>
        </div>
        <div className="relative h-44 sm:h-56">
          <img src={t.image} alt={t.destination} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 to-transparent" />
          <div className="absolute bottom-5 left-6 right-6 text-white sm:left-8">
            <p className="text-xs text-white/70">Prepared for {c.name}</p>
            <h1 className="font-display text-2xl font-bold sm:text-3xl" data-testid="quote-title">{t.title}</h1>
            <p className="text-sm text-white/80">{t.vessel} · {fmtDate(t.departure)} – {fmtDate(returnDate(t))}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-8 p-6 sm:p-8 lg:grid-cols-5">
          <div className={editInvoice || editItin ? "lg:col-span-5" : "lg:col-span-3"}>
            <SectionHead title="Invoice" editing={editInvoice} onEdit={() => setEditInvoice(true)} testId="quote-edit-invoice-btn" />
            {editInvoice ? <InvoiceBuilder trip={t} onDone={() => setEditInvoice(false)} /> : t.items?.length === 0 ? (
              <button onClick={() => setEditInvoice(true)} data-testid="quote-build-invoice-btn" className="w-full rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500 hover:border-navy-300 hover:text-navy-900">No charges yet. Build the invoice for this quote.</button>
            ) : <LineItems trip={t} />}
            <div className="mt-8"><SectionHead title="Itinerary Overview" editing={editItin} onEdit={() => setEditItin(true)} testId="quote-edit-itinerary-btn" /></div>
            {editItin ? <ItineraryEditor trip={t} onDone={() => setEditItin(false)} /> : (
              <ol className="space-y-2" data-testid="quote-itinerary-overview">
                {itinerary(t).map((d) => (
                  <li key={d.day} className="flex gap-3 text-sm"><span className="w-12 shrink-0 text-xs font-semibold leading-5 text-slate-400">Day {d.day}</span><span className="min-w-0 text-navy-900">{d.title}{d.place !== d.title && <span className="text-slate-400"> · {d.place}</span>}</span></li>
                ))}
              </ol>
            )}
          </div>
          <div className="space-y-5 lg:col-span-2">
            <div className="rounded-2xl bg-navy-900 p-5 text-white">
              <p className="text-xs text-white/60">Trip total</p>
              <p className="font-display text-3xl font-bold" data-testid="quote-total">{fmtMoney(tripTotal(t), true)}</p>
              <p className="mt-2 text-xs text-white/60">{fmtMoney(deposit, true)} deposit to confirm · {t.travelers?.length || 0} travelers · {t.nights} nights</p>
            </div>
            <Card className="shadow-none"><CardTitle action={!editPlan && <button onClick={() => setEditPlan(true)} data-testid="quote-edit-plan-btn" className={btn.ghost}><Pencil className="h-3.5 w-3.5" />Edit</button>}>Payment plan</CardTitle><div className="p-5">{editPlan ? <PaymentPlanEditor trip={t} onDone={() => setEditPlan(false)} /> : <PaymentSchedule trip={t} />}</div></Card>
            <CommissionCard key={t.commission ?? "auto"} trip={t} />
          </div>
        </div>
        {t.status === "QUOTE" && <QuoteTerms />}
      </Card>
      {editDetails && <NewQuoteDialog open trip={t} onOpenChange={setEditDetails} />}
    </div>
  );
}
